import * as THREE from 'three';
import { itemFxCalm } from '@/entities/fx/itemFx';

/**
 * ShieldBubble.ts — the SHIELD item's bubble (M145 rebuild). The M12 version
 * was a flat additive sphere at 20% opacity: on a bright course it was close
 * to invisible, and nothing happened when it actually saved you.
 *
 * Now an energy shell: bright at the rim and clear in the middle (so you can
 * still see your car and ball through it), a honeycomb skin drifting over it,
 * a scale pop when it goes up, and a white FLASH when it blocks a hit.
 * One draw call, no textures. Presentation only.
 */

const VERT = /* glsl */ `
  varying vec3 vNormal;
  varying vec3 vView;
  varying vec3 vPos;
  void main() {
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vNormal = normalize(normalMatrix * normal);
    vView = normalize(-mv.xyz);
    vPos = position;
    gl_Position = projectionMatrix * mv;
  }
`;

const FRAG = /* glsl */ `
  uniform float uTime;
  uniform float uFade;
  uniform float uFlash;
  uniform vec3 uColor;
  varying vec3 vNormal;
  varying vec3 vView;
  varying vec3 vPos;

  // Distance to the nearest honeycomb cell edge (0 on the edge).
  float hexEdge(vec2 p) {
    vec2 r = vec2(1.0, 1.7320508);
    vec2 h = r * 0.5;
    vec2 a = mod(p, r) - h;
    vec2 b = mod(p - h, r) - h;
    vec2 g = dot(a, a) < dot(b, b) ? a : b;
    g = abs(g);
    return 0.5 - max(dot(g, normalize(r)), g.x);
  }

  void main() {
    float facing = abs(dot(normalize(vNormal), normalize(vView)));
    float rim = pow(1.0 - facing, 2.4);
    vec3 n = normalize(vPos);
    vec2 uv = vec2(atan(n.z, n.x) * 3.2 + uTime * 0.25, n.y * 5.5);
    float cell = 1.0 - smoothstep(0.0, 0.07, hexEdge(uv));
    // A band of light sweeping up the shell.
    float sweep = smoothstep(0.75, 1.0, sin(n.y * 3.0 - uTime * 2.2));
    // The block flash lights the SKIN (rim + cells) rather than filling the
    // dome: a full white-out hid the car and ball for a beat (measured).
    float alpha = (0.07 + rim * 0.8 + cell * (0.16 + 0.34 * rim) + sweep * 0.1 + uFlash * (0.16 + 0.5 * cell)) * uFade;
    vec3 col = mix(uColor, vec3(1.0), clamp(rim * 0.45 + uFlash * 0.7, 0.0, 1.0)) * (1.0 + rim * 0.9 + uFlash * 0.9);
    gl_FragColor = vec4(col, clamp(alpha, 0.0, 1.0));
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

export class ShieldBubble {
  readonly mesh: THREE.Mesh;
  private readonly mat: THREE.ShaderMaterial;
  private fade = 0;
  private target = 0;
  private pop = 0;
  private flash = 0;
  private t = 0;

  constructor() {
    this.mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uTime: { value: 0 },
        uFade: { value: 0 },
        uFlash: { value: 0 },
        uColor: { value: new THREE.Color(0x6ef0b9).multiplyScalar(1.3) },
      },
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    this.mesh = new THREE.Mesh(new THREE.SphereGeometry(2.7, 28, 18), this.mat);
    this.mesh.position.y = 0.85;
    this.mesh.renderOrder = 4;
    this.mesh.visible = false;
  }

  set(on: boolean): void {
    if (on && this.target === 0) this.pop = 1; // going up: a scale pop
    this.target = on ? 1 : 0;
  }

  /** The shield just turned a hit away. */
  block(): void {
    if (this.target > 0) this.flash = 1;
  }

  update(dt: number): void {
    this.fade += (this.target - this.fade) * (1 - Math.exp(-8 * dt));
    this.mesh.visible = this.fade > 0.02;
    if (!this.mesh.visible) return;
    this.t += dt * (itemFxCalm() ? 0.25 : 1);
    this.pop *= Math.exp(-7 * dt);
    this.flash *= Math.exp(-5 * dt);
    this.mat.uniforms.uTime.value = this.t;
    this.mat.uniforms.uFade.value = this.fade;
    this.mat.uniforms.uFlash.value = this.flash;
    const s = 1 + Math.sin(this.t * 5) * 0.025 + this.pop * 0.22 + this.flash * 0.1;
    this.mesh.scale.setScalar(s);
  }
}
